import { useMemo } from 'react'
import Box from '@mui/material/Box'
import SvgIcon from '@mui/material/SvgIcon'
import { createTheme as extendTheme, useTheme, type Theme } from '@mui/material/styles'
import { DotsGrid } from '@untitled-ui/icons-react'
import { VIEWER_UPPER_CONTROL_HEIGHT } from '@/components/Viewer/ViewerUpper/viewerUpperToolbarStyles'
import { stepAssistantTypography } from '@/components/NabuAssistant/stepAssistantTheme'
import { createTheme } from '@/theme'

/*
 * Famille des fenêtres de /declaration : en-tête sombre à l'aurore, corps aux couleurs du thème.
 * Violet pour l'Assistant Nabu (l'IA), bleu de la sidenav pour les outils (panneau de contrôle,
 * document source).
 */

/** Hauteur commune des en-têtes de fenêtre, celle de l'Assistant Nabu. */
export const DECLARATION_WINDOW_HEADER_HEIGHT = 34

/** Gabarit commun des en-têtes : hauteur, marges et écart entre éléments. */
export const declarationWindowHeaderSx = {
  height: DECLARATION_WINDOW_HEADER_HEIGHT,
  minHeight: DECLARATION_WINDOW_HEADER_HEIGHT,
  boxSizing: 'border-box',
  px: '8px',
  py: 0,
  gap: 1,
} as const

/*
 * Ombres des fenêtres. En sombre, une ombre noire se perd sur un fond presque noir : l'ombre
 * y est claire, un halo blanc diffus.
 */
export const declarationFloatingWindowShadow = (theme: Theme) =>
  theme.palette.mode === 'light'
    ? '0px 8px 24px rgba(0,0,0,0.3)'
    : '0px 8px 32px rgba(255,255,255,0.10), 0 0 0 1px rgba(255,255,255,0.15)'

export const declarationDockedWindowShadow = (theme: Theme) =>
  theme.palette.mode === 'light'
    ? theme.shadows[2]
    : '0px 2px 14px rgba(255,255,255,0.07)'

/** Titre d'en-tête, repris par le sélecteur de pièce du document source. */
export const declarationWindowTitleSx = {
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.2,
} as const

/**
 * Retrait du titre d'une fenêtre épinglée : le texte du sélecteur de pièce du document source
 * (bordure 1 px + marge interne 10 px), pour que les titres s'alignent d'une fenêtre à l'autre.
 */
export const DECLARATION_WINDOW_DOCKED_TITLE_INSET = '11px'

/** Poignée de déplacement d'une fenêtre flottante, à la taille d'un bouton d'en-tête. */
export const DeclarationWindowDragHandle = ({ testId }: { testId?: string }) => (
  <Box
    data-testid={testId}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      flexShrink: 0,
      width: VIEWER_UPPER_CONTROL_HEIGHT,
      height: VIEWER_UPPER_CONTROL_HEIGHT,
    }}
  >
    <SvgIcon sx={{ fontSize: 13 }}>
      <DotsGrid />
    </SvgIcon>
  </Box>
)

/** Bleu de l'élément actif de la sidenav (`--nav-item-active-bg`, rgba(19, 98, 132)). */
export const NABU_TOOL_BLUE = '#136284'

/** Thème sombre pour un en-tête : ses boutons, menus et infobulles suivent. */
export const useDarkHeaderTheme = () => {
  const theme = useTheme()
  return useMemo(
    () => createTheme({ paletteMode: 'dark', direction: theme.direction }),
    [theme.direction]
  )
}

/** Le thème courant, tailles de texte ramenées à l'échelle de l'Assistant Nabu (12 / 10 px). */
export const useAssistantScaleTheme = () => {
  const theme = useTheme()
  return useMemo(() => {
    const body = {
      fontSize: stepAssistantTypography.body,
      lineHeight: stepAssistantTypography.bodyLineHeight,
    }
    return extendTheme(theme, {
      typography: {
        body1: body,
        body2: body,
        subtitle1: { ...body, fontWeight: 600 },
        subtitle2: { ...body, fontWeight: 600 },
        caption: { fontSize: stepAssistantTypography.caption, lineHeight: 1.4 },
        overline: { fontSize: stepAssistantTypography.caption },
        h6: { fontSize: 14, lineHeight: 1.35 },
      },
    })
  }, [theme])
}
